import { useState } from 'react';

import { Permission, initialsFa, toPersianDigits } from '@bookora/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  Grid,
  Icon,
  Phone,
  Space,
  Spin,
  Tag,
  Typography,
  useConfirm,
} from '@bookora/ui';
import type { MenuProps } from '@bookora/ui';
import { useNavigate } from 'react-router-dom';

import { logout } from '@/api/endpoints/auth';
import { useAuth, useSessionStore } from '@/auth/token-store';
import { SupportDialog } from '@/components/support-contact';
import { tourAnchor, useHelpMenuItems, useHubBadgeTotal } from '@/guidance';
import { toast } from '@/lib/feedback';
import { useCan } from '@/permissions/use-can';

/**
 * Identity, role, and the way out.
 *
 * Signing out revokes the refresh token on the server before clearing the
 * client. The order matters: clearing first would leave a live refresh token in
 * the database that nobody can revoke from this device any more, and the user's
 * session would keep appearing in «دستگاه‌های فعال» until it expired on its own.
 */
export function UserMenu() {
  const { user, activeClinic } = useAuth();
  const refreshToken = useSessionStore((state) => state.refreshToken);
  const clearSession = useSessionStore((state) => state.clearSession);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const canOpenSettings = useCan(Permission.SETTINGS_VIEW);
  const [signingOut, setSigningOut] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  // On a phone the header is ~390px and this trigger used to take ~200 of it
  // for the operator's OWN name, squeezing the clinic label to «…». Below `md`
  // the avatar alone is the trigger; the name is in the dropdown's header.
  const screens = Grid.useBreakpoint();
  const showName = screens.md !== false;
  // Below `md` the header has no room for the «راهنما» menu, so its items live
  // here (and in the nav drawer), and the hub's "something new" dot rides on
  // the avatar instead of on the Help trigger.
  const isCompact = screens.md === false;
  const helpItems = useHelpMenuItems();
  const hubBadge = useHubBadgeTotal();

  if (!user) return null;

  async function handleSignOut(): Promise<void> {
    const confirmed = await confirm({
      title: 'از حساب خارج می‌شید؟',
      content: 'برای ورود دوباره باید کد پیامکی جدید دریافت کنید.',
      okText: 'خروج از حساب',
      cancelText: 'ماندن در پنل',
      // Worth a pause, but nothing is destroyed: Cancel takes the focus
      // without dressing «خروج» as a delete.
      focusCancel: true,
    });
    if (!confirmed) return;

    setSigningOut(true);
    try {
      if (refreshToken) await logout(refreshToken);
    } catch (error) {
      // A network failure must not trap the user in a session they asked to
      // leave; the local session is dropped either way and the token expires
      // on its own.
      toast.error(error);
    } finally {
      clearSession();
      queryClient.clear();
      setSigningOut(false);
      void navigate('/login', { replace: true });
    }
  }

  const items: MenuProps['items'] = [
    {
      key: 'identity',
      type: 'group',
      label: (
        <div
          style={{
            paddingBlock: 'var(--bk-space-1)',
            maxInlineSize: 260,
            overflowWrap: 'anywhere',
          }}
        >
          <div style={{ color: 'var(--bk-fg)', fontWeight: 'var(--bk-weight-medium)' }}>
            {user.fullName ?? 'حساب من'}
          </div>
          <div className="bk-subtle" style={{ fontSize: 'var(--bk-text-caption)' }}>
            <Phone value={user.phone} />
          </div>
          {activeClinic?.roleName ? (
            <Tag style={{ marginBlockStart: 'var(--bk-space-1-5)' }}>{activeClinic.roleName}</Tag>
          ) : null}
          {user.twoFactorEnabled ? (
            <Tag
              color="success"
              icon={<Icon name="shield" />}
              style={{ marginBlockStart: 'var(--bk-space-1-5)' }}
            >
              تأیید دومرحله‌ای فعال
            </Tag>
          ) : null}
        </div>
      ),
    },
    { type: 'divider' },
    ...(canOpenSettings
      ? [
          {
            key: 'settings',
            icon: <Icon name="settings" />,
            label: 'تنظیمات کلینیک',
          },
        ]
      : []),
    /*
     * Always present, deliberately.
     *
     * «تنظیمات کلینیک» is gated on `settings.view`; the account's own security
     * is not gated on anything, because two-factor enrolment and revoking a
     * stolen session belong to the person signed in rather than to the clinic.
     * Without this entry a منشی or پرستار — neither preset holds
     * `settings.view` — had no way at all to reach the screen, while the badge
     * above went on telling them whether their second factor was on.
     */
    {
      key: 'security',
      icon: <Icon name="shield" />,
      label: 'امنیت حساب',
    },
    /*
     * The way to a human. Several server messages end «با پشتیبانی تماس
     * بگیرید», and until this entry existed there was nowhere in the panel to
     * find out how. Above «خروج» so it is not mistaken for part of signing out.
     */
    {
      key: 'support',
      icon: <Icon name="chat" />,
      label: 'تماس با پشتیبانی',
    },
    ...(isCompact && helpItems.length > 0
      ? [
          { type: 'divider' as const },
          { key: 'help', type: 'group' as const, label: 'راهنما', children: helpItems },
        ]
      : []),
    { type: 'divider' },
    {
      key: 'logout',
      icon: <Icon name="logout" />,
      label: 'خروج از حساب',
      danger: true,
    },
  ];

  return (
    <>
      <Dropdown
        trigger={['click']}
        // `bottom-end`: this trigger is at the inline END of the header, so
        // the panel must hang from that edge or it runs off the viewport.
        // Same pixels as the old physical `bottomLeft` in RTL, and the
        // right answer rather than a coincidence.
        placement="bottom-end"
        menu={{
          items,
          onClick: ({ key }) => {
            if (key === 'settings') void navigate('/settings');
            if (key === 'security') void navigate('/settings/security');
            if (key === 'support') setSupportOpen(true);
            if (key === 'logout') void handleSignOut();
          },
        }}
      >
        <Button
          {...tourAnchor('shell.user-menu')}
          type="text"
          style={{ height: 40, minWidth: 40, paddingInline: showName ? 8 : 6 }}
          // Not `loading`: AntD inserts its spinner beside the avatar, the
          // trigger grows 22px and pushes the bell, the theme toggle and the
          // clinic label along the header. Progress is shown inside the fixed
          // 28px avatar instead, so the trigger's box never changes.
          disabled={signingOut}
          aria-busy={signingOut}
          aria-label={
            showName
              ? undefined
              : `حساب کاربری: ${user.fullName ?? 'حساب من'}` +
                (isCompact && hubBadge > 0 ? `، ${toPersianDigits(hubBadge)} پیشنهاد جدید` : '')
          }
        >
          <Space size={8}>
            {/* `color` is not decoration here: AntD's Avatar defaults to white
              text, which vanishes on the dark theme's paper primary.
              `--bk-on-primary` is the contract-bound token — white on the
              light theme's ink, ink on the dark theme's paper. */}
            <Badge dot={isCompact && hubBadge > 0}>
              <Avatar
                size={28}
                style={{ backgroundColor: 'var(--bk-primary)', color: 'var(--bk-on-primary)' }}
              >
                {/* `inherit`: Spin paints its dots in `currentColor` over a root
                colour of primary — the avatar's own fill. Inheriting takes
                the avatar's `--bk-on-primary` so it stays visible. */}
                {signingOut ? (
                  <Spin size="small" style={{ color: 'inherit' }} />
                ) : (
                  initialsFa(user.fullName ?? '') || '؟'
                )}
              </Avatar>
            </Badge>
            {showName ? (
              <>
                <Typography.Text className="bk-truncate" style={{ maxInlineSize: 140 }}>
                  {user.fullName ?? 'حساب من'}
                </Typography.Text>
                <Icon name="chevron-down" size={16} />
              </>
            ) : null}
          </Space>
        </Button>
      </Dropdown>
      <SupportDialog open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}
